import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthService, ResponseObject } from '../../Services/auth.service';
import { NotificationsService } from '../../Services/shared/notifications.service';
import { NotificationTypes } from '../../app.enums';
import { TranslocoService } from '@jsverse/transloco';
import { slideDown } from './header.animations';

interface MegaMenuItem {
  label: string;
  icon?: string;
  routerLink?: string[];
  megaMenu?: MegaMenuSection[];
  badge?: number;
  visible: boolean;
  cssClass?: string;
}

interface MegaMenuSection {
  title: string;
  items: MegaMenuLink[];
  featured?: boolean;
  columns?: number;
}

interface MegaMenuLink {
  label: string;
  icon?: string;
  routerLink: string[];
  description?: string;
  badge?: number;
  visible: boolean;
}

@Component({
    selector: 'ds-gallery-header',
    templateUrl: './ds-gallery-header.component.html',
    styleUrls: ['./ds-gallery-header.component.scss'],
    animations: [slideDown],
    standalone: false
})
export class DsGalleryHeaderComponent implements OnInit {
  menuItems: MegaMenuItem[] = [];
  activeMenu: string | null = null;
  authUser: any;
  authConfig: any;

  // User dropdown state
  isUserMenuOpen = false;
  isFreePlanUser = false;
  proBySource: string = '';
  viewingRoleAsAdmin = false;

  // Mobile menu state
  isMobileMenuOpen = false;
  expandedMobileMenu: string | null = null;

  // Messages visibility
  showMessagesIcon = false;

  // Notification inputs from parent (app.component)
  @Input() chatmessages: any[] = [];
  @Input() notifications: any[] = [];
  @Input() countUnreadchatbox: number = 0;
  @Input() countUnreadNotificaion: number = 0;
  @Input() isChatBoxMessagesShow: boolean = false;
  @Input() showMessages: boolean = false;
  @Input() showNotifications: boolean = false;
  @Input() isLoadingMessages: boolean = false;
  @Input() isLoadingNotifications: boolean = false;

  // Free trial inputs from parent
  @Input() isOnFreeTrial: boolean = false;
  @Input() hasFreeTrialEnded: boolean = false;
  @Input() remainingTime: any = {};

  // Output events for parent
  @Output() toggleMessagesEvent = new EventEmitter<Event>();
  @Output() toggleNotificationsEvent = new EventEmitter<Event>();
  @Output() chatWithUserEvent = new EventEmitter<any>();
  @Output() handleNotificationClickEvent = new EventEmitter<any>();
  @Output() navigateToMessagesListEvent = new EventEmitter<void>();
  @Output() navigateToNotificationsListEvent = new EventEmitter<void>();
  @Output() startFreeTrialEvent = new EventEmitter<void>();
  @Output() showUpgradeDialogEvent = new EventEmitter<void>();
  @Output() toggleSidebarEvent = new EventEmitter<void>();

  constructor(
    private authService: AuthService,
    private router: Router,
    private ngxSpinnerService: NgxSpinnerService,
    private notificationsService: NotificationsService,
    private transloco: TranslocoService
  ) {}

  ngOnInit() {
    this.authUser = this.authService.getCurrentUser();
    this.authConfig = this.authService.getAuthConfig();
    this.isFreePlanUser = this.authService.isFreePlanUser();
    this.proBySource = this.authService.proBySource();
    this.viewingRoleAsAdmin = this.authService.isViewingAsAdministrator();
    this.initializeMenuItems();
  }

  private initializeMenuItems() {
    const authConfig = this.authService.getAuthConfig();
    const isGallery = !!authConfig.IsGallery;
    const isGalleryStaff = !!authConfig.IsGalleryStaff;
    const isChatboxEnabled = this.authService.IsChatboxMessages();

    // Set messages icon visibility
    this.showMessagesIcon = (isGallery || isGalleryStaff) && isChatboxEnabled;

    this.menuItems = [
      // Dashboard - Direct Link
      {
        label: 'Dashboard',
        icon: 'pi pi-home',
        routerLink: ['/ds-gallery-dashboard'],
        visible: isGallery || isGalleryStaff
      },

      // Artworks - Mega Menu
      {
        label: 'Artworks',
        icon: 'pi pi-images',
        visible: isGallery || isGalleryStaff,
        megaMenu: [
          {
            title: 'Manage',
            items: [
              { label: 'Consignment Products', icon: 'pi pi-briefcase', routerLink: ['/galleries', 'gallery-products'], description: 'View consigned artworks', visible: true },
              { label: 'Gallery Products', icon: 'pi pi-th-large', routerLink: ['/galleries', 'view-gallery-products'], description: 'Your own inventory', visible: isGallery },
              { label: 'Create Product', icon: 'pi pi-plus-circle', routerLink: ['/galleries', 'gallery-product', 'create'], description: 'Add gallery product', visible: isGallery },
              { label: 'Requested Products', icon: 'pi pi-inbox', routerLink: ['/galleries', 'requested-products'], description: 'Product requests', visible: true },
              { label: 'Labels Generation', icon: 'pi pi-barcode', routerLink: ['/labels-generation'], description: 'Info cards & barcodes', visible: isGallery }
            ]
          },
          {
            title: 'Events',
            items: [
              { label: 'Gallery Events', icon: 'pi pi-trophy', routerLink: ['/events'], description: 'Manage events', visible: isGallery },
              { label: 'Create Event', icon: 'pi pi-plus-circle', routerLink: ['/events', 'create'], description: 'New exhibition or competition', visible: isGallery }
            ]
          }
        ]
      },

      // Connect - Mega Menu
      {
        label: 'Connect',
        icon: 'pi pi-link',
        visible: isGallery || isGalleryStaff,
        megaMenu: [
          {
            title: 'Artists',
            items: [
              { label: 'Connected Artists', icon: 'pi pi-users', routerLink: ['/galleries', 'connected-artists'], description: 'Your artist partners', visible: true },
              { label: 'Proposals', icon: 'pi pi-file', routerLink: ['/galleries', 'view-proposals'], description: 'Artwork proposals', visible: true },
              { label: 'Consignments', icon: 'pi pi-truck', routerLink: ['/view-consignments'], description: 'Dispatch documents', visible: true }
            ]
          },
          {
            title: 'Portfolio',
            items: [
              { label: 'Gallery Portfolio', icon: 'pi pi-folder', routerLink: ['/galleries', 'portfolio'], description: 'Manage portfolio', visible: isGallery },
              { label: 'Shareable Products', icon: 'pi pi-share-alt', routerLink: ['/galleries', 'gallery-shareable-product'], description: 'Share with clients', visible: isGallery }
            ]
          }
        ]
      },

      // Settings - Mega Menu
      {
        label: 'Settings',
        icon: 'pi pi-cog',
        visible: isGallery,
        megaMenu: [
          {
            title: 'Gallery Setup',
            items: [
              { label: 'Gallery Staff', icon: 'pi pi-users', routerLink: ['/gallery-staff'], description: 'Manage staff members', visible: true },
              { label: 'Gallery Profile', icon: 'pi pi-building', routerLink: ['/edit-profile'], description: 'Update gallery info', visible: true }
            ]
          },
          {
            title: 'Analytics',
            items: [
              { label: 'Legacy Dashboard', icon: 'pi pi-chart-bar', routerLink: ['/dashboard'], description: 'Classic dashboard', visible: true }
            ]
          }
        ]
      }
    ];

    // Filter out invisible items
    this.menuItems = this.menuItems.filter(item => item.visible);
    this.menuItems.forEach(item => {
      if (item.megaMenu) {
        item.megaMenu.forEach(section => {
          section.items = section.items.filter(link => link.visible);
        });
        item.megaMenu = item.megaMenu.filter(section => section.items.length > 0);
      }
    });
  }

  toggleMegaMenu(label: string, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.activeMenu = this.activeMenu === label ? null : label;
  }

  openMegaMenu(label: string) {
    this.activeMenu = label;
  }

  closeMegaMenu() {
    this.activeMenu = null;
  }

  isActive(item: MegaMenuItem): boolean {
    if (item.routerLink) {
      return this.router.isActive(item.routerLink.join('/'), false);
    }
    return false;
  }

  isMenuItemActive(item: MegaMenuItem): boolean {
    if (item.routerLink && this.router.isActive(item.routerLink.join('/'), false)) {
      return true;
    }

    if (item.megaMenu) {
      for (const section of item.megaMenu) {
        for (const link of section.items) {
          if (this.router.isActive(link.routerLink.join('/'), false)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  // User menu methods
  toggleUserMenu(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.isUserMenuOpen = !this.isUserMenuOpen;
    if (this.isUserMenuOpen) {
      this.closeMegaMenu();
    }
  }

  closeUserMenu() {
    this.isUserMenuOpen = false;
  }

  // Mobile menu methods
  toggleMobileMenu(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    if (this.isMobileMenuOpen) {
      this.closeMegaMenu();
      this.closeUserMenu();
    }
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
    this.expandedMobileMenu = null;
  }

  toggleMobileSubMenu(label: string) {
    this.expandedMobileMenu = this.expandedMobileMenu === label ? null : label;
  }

  logout(event: Event) {
    event.preventDefault();
    this.closeUserMenu();
    this.authService.logout().subscribe((res) => {
      window.location.reload();
    });
  }

  exitRoleView(event: Event) {
    event.preventDefault();
    this.ngxSpinnerService.show();
    this.authService.exitAccount().subscribe(
      (response: ResponseObject) => {
        this.ngxSpinnerService.hide();
        if (response.Success) {
          this.router.navigate(['/ds-admin-dashboard']).then(() => {
            location.reload();
          });
          return;
        }
        this.notificationsService.showNotification(this.transloco.translate('dsGalleryHeader.error'), response.ResponseMessage, NotificationTypes.Error);
      },
      (error) => {
        console.error(error);
        this.ngxSpinnerService.hide();
      }
    );
  }

  get userFullName(): string {
    if (!this.authUser) return '';
    return `${this.authUser.FirstName || ''} ${this.authUser.LastName || ''}`.trim() || 'Gallery';
  }

  get planDisplayText(): string {
    if (this.authConfig?.IsGallery) {
      return this.isFreePlanUser ? 'Gallery Free' : 'Gallery Pro';
    }
    if (this.authConfig?.IsSuperAdmin) {
      return 'Super Admin';
    }
    if (this.authConfig?.IsAdministrator) {
      return 'Administrator';
    }
    return '';
  }

  get showSubscriptionsLink(): boolean {
    return this.authConfig?.IsGallery &&
           !this.isFreePlanUser &&
           this.proBySource !== 'Artflow' &&
           this.proBySource !== 'FreeTrial';
  }

  // Notification methods - emit events to parent
  onToggleMessages(event: Event) {
    this.toggleMessagesEvent.emit(event);
  }

  onToggleNotifications(event: Event) {
    this.toggleNotificationsEvent.emit(event);
  }

  onChatWithUser(message: any) {
    this.chatWithUserEvent.emit(message);
  }

  onHandleNotificationClick(notification: any) {
    this.handleNotificationClickEvent.emit(notification);
  }

  onNavigateToMessagesList() {
    this.navigateToMessagesListEvent.emit();
  }

  onNavigateToNotificationsList() {
    this.navigateToNotificationsListEvent.emit();
  }

  onStartFreeTrial() {
    this.startFreeTrialEvent.emit();
  }

  onShowUpgradeDialog() {
    this.showUpgradeDialogEvent.emit();
  }
}
